/**
 * VisitorsSection Component
 * Dynamic visitor entries for daily logs
 * BuilderBel Construction Project Management System
 */

import { ChevronDown, ChevronRight, Plus, Trash2, UserCheck } from 'lucide-react';
import React from 'react';
import { Control, Controller, FieldErrors, UseFieldArrayReturn } from 'react-hook-form';
import type { VisitorEntry } from '../../../types/dailyLog';
import { Input, TextArea, TimePicker } from '../../forms';

// Common visitor purposes
const PURPOSE_OPTIONS = [
  'Site Inspection',
  'Project Meeting',
  'Safety Inspection',
  'Client Walkthrough',
  'Architect Visit',
  'Engineer Visit',
  'Permit Inspection',
  'Building Inspector',
  'Fire Marshal',
  'Utility Company',
  'Delivery',
  'Subcontractor Meeting',
  'Progress Photos',
  'Training',
  'Other',
];

interface VisitorsSectionProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errors: FieldErrors<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fieldArray: UseFieldArrayReturn<any, 'visitors', 'id'>;
  isExpanded: boolean;
  onToggle: () => void;
  disabled?: boolean;
}

// Helper to format time for input
const formatTimeForInput = (isoString?: string): string => {
  if (!isoString) return '';
  try {
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
};

// Helper to create ISO string from date and time
const createISOFromTime = (baseDate: string, time: string): string => {
  if (!time) return '';
  try {
    const [hours, minutes] = time.split(':');
    const date = new Date(baseDate);
    date.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
    return date.toISOString();
  } catch {
    return '';
  }
};

export const VisitorsSection: React.FC<VisitorsSectionProps> = ({
  control,
  errors,
  fieldArray,
  isExpanded,
  onToggle,
  disabled = false,
}) => {
  const { fields, append, remove } = fieldArray;
  const visitorErrors = errors.visitors as unknown as FieldErrors<VisitorEntry>[] | undefined;

  const handleAddEntry = () => {
    const now = new Date().toISOString();
    append({
      name: '',
      company: '',
      purpose: '',
      timeIn: now,
      timeOut: '',
      badgeNumber: '',
      escortedBy: '',
      notes: '',
    });
  };

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Section Header */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-4 py-3 bg-gray-50 flex items-center justify-between text-left hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center space-x-3">
          <UserCheck className="h-5 w-5 text-gray-500" />
          <span className="font-medium text-gray-900">Visitors</span>
          <span className="text-sm text-gray-500">
            ({fields.length} {fields.length === 1 ? 'visitor' : 'visitors'})
          </span>
        </div>
        {isExpanded ? (
          <ChevronDown className="h-5 w-5 text-gray-400" />
        ) : (
          <ChevronRight className="h-5 w-5 text-gray-400" />
        )}
      </button>

      {/* Section Content */}
      {isExpanded && (
        <div className="p-4 space-y-4 bg-white">
          {/* Entries */}
          {fields.map((field, index) => (
            <div
              key={field.id}
              className="p-4 border border-gray-200 rounded-lg bg-gray-50 space-y-4"
            >
              {/* Entry Header */}
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Visitor #{index + 1}</span>
                {!disabled && fields.length > 0 && (
                  <button
                    type="button"
                    onClick={() => remove(index)}
                    className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                    title="Remove entry"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>

              {/* Name & Company */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Controller
                  name={`visitors.${index}.name`}
                  control={control}
                  render={({ field: inputField }) => (
                    <Input
                      label="Visitor Name"
                      value={inputField.value}
                      onChange={inputField.onChange}
                      error={visitorErrors?.[index]?.name?.message}
                      disabled={disabled}
                      required
                      placeholder="Full name"
                    />
                  )}
                />

                <Controller
                  name={`visitors.${index}.company`}
                  control={control}
                  render={({ field: inputField }) => (
                    <Input
                      label="Company/Organization"
                      value={inputField.value}
                      onChange={inputField.onChange}
                      error={visitorErrors?.[index]?.company?.message}
                      disabled={disabled}
                      required
                      placeholder="Company name"
                    />
                  )}
                />
              </div>

              {/* Purpose & Badge */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Controller
                  name={`visitors.${index}.purpose`}
                  control={control}
                  render={({ field: inputField }) => (
                    <div>
                      <Input
                        label="Purpose of Visit"
                        value={inputField.value}
                        onChange={inputField.onChange}
                        error={visitorErrors?.[index]?.purpose?.message}
                        disabled={disabled}
                        required
                        placeholder="Select or enter purpose"
                        list={`purpose-options-${index}`}
                      />
                      <datalist id={`purpose-options-${index}`}>
                        {PURPOSE_OPTIONS.map(purpose => (
                          <option key={purpose} value={purpose} />
                        ))}
                      </datalist>
                    </div>
                  )}
                />

                <Controller
                  name={`visitors.${index}.badgeNumber`}
                  control={control}
                  render={({ field: inputField }) => (
                    <Input
                      label="Badge Number"
                      value={inputField.value || ''}
                      onChange={inputField.onChange}
                      error={visitorErrors?.[index]?.badgeNumber?.message}
                      disabled={disabled}
                      placeholder="Visitor badge #"
                    />
                  )}
                />
              </div>

              {/* Time In/Out & Escorted By */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Controller
                  name={`visitors.${index}.timeIn`}
                  control={control}
                  render={({ field: inputField }) => (
                    <TimePicker
                      label="Time In"
                      required
                      value={formatTimeForInput(inputField.value)}
                      onChange={val => {
                        const logDate =
                          control._formValues?.logDate || new Date().toISOString().split('T')[0];
                        inputField.onChange(createISOFromTime(logDate, val));
                      }}
                      disabled={disabled}
                    />
                  )}
                />

                <Controller
                  name={`visitors.${index}.timeOut`}
                  control={control}
                  render={({ field: inputField }) => (
                    <TimePicker
                      label="Time Out"
                      value={formatTimeForInput(inputField.value)}
                      onChange={val => {
                        const logDate =
                          control._formValues?.logDate || new Date().toISOString().split('T')[0];
                        inputField.onChange(createISOFromTime(logDate, val));
                      }}
                      disabled={disabled}
                    />
                  )}
                />

                <Controller
                  name={`visitors.${index}.escortedBy`}
                  control={control}
                  render={({ field: inputField }) => (
                    <Input
                      label="Escorted By"
                      value={inputField.value || ''}
                      onChange={inputField.onChange}
                      error={visitorErrors?.[index]?.escortedBy?.message}
                      disabled={disabled}
                      placeholder="Staff name"
                    />
                  )}
                />

                <div className="md:col-span-1" />
              </div>

              {/* Notes */}
              <Controller
                name={`visitors.${index}.notes`}
                control={control}
                render={({ field: inputField }) => (
                  <TextArea
                    label="Notes"
                    value={inputField.value || ''}
                    onChange={inputField.onChange}
                    error={visitorErrors?.[index]?.notes?.message}
                    disabled={disabled}
                    rows={2}
                    placeholder="Additional notes about the visit..."
                  />
                )}
              />
            </div>
          ))}

          {/* Add Entry Button */}
          {!disabled && (
            <button
              type="button"
              onClick={handleAddEntry}
              className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-brand-500 hover:text-brand-600 transition-colors flex items-center justify-center space-x-2"
            >
              <Plus className="h-5 w-5" />
              <span>Log Visitor</span>
            </button>
          )}

          {/* Empty State */}
          {fields.length === 0 && disabled && (
            <div className="text-center py-6 text-gray-500">No visitors logged</div>
          )}
        </div>
      )}
    </div>
  );
};

export default VisitorsSection;
